import { useEffect } from "react";
import { useUser } from "@/hooks/firestore/user/useUser";
import { FirestoreError } from "firebase/firestore";
import {
  CustomerLicenceOptions,
  useAuthStore,
} from "@/features/auth/stores/authStore";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useCustomerLicence } from "@/features/auth/hooks/useCustomerLicence";
import * as Session from "@/features/auth/services/session";
import { useIsVerified } from "@/hooks/useIsVerified";

const useProfile = () => {
  const { user, isLoading, error } = useUser();
  const userId = useUserId();
  const isSigningOut = useAuthStore((s) => s.isSigningOut);
  const isRefreshing = useAuthStore((s) => s.isRefreshing);

  useEffect(() => {
    if (!isSigningOut && !isRefreshing && userId) {
      // the engine types errors as Error; session checks only apply to
      // Firestore auth errors, which are FirestoreError instances at runtime
      if (error instanceof FirestoreError) {
        checkSession(error, userId, () => {
          Session.signOut();
        });
      }
    }
  }, [user, error, isSigningOut, isRefreshing]);

  return {
    profile: user?.profile,
    phoneDetails: user?.phoneDetails,
    isLoading,
  };
};

type Profile = Exclude<ReturnType<typeof useProfile>["profile"], undefined>;

const useProfileAddress = () => {
  const { profile, isLoading } = useProfile();

  const address = profile
    ? [
        profile.unit,
        profile.unit ? "/" : "",
        profile.streetNumber,
        " ",
        profile.street,
        ", ",
        profile.suburb,
        ", ",
        profile.state,
        profile.state ? " " : "",
        profile.postCode,
        ", ",
        profile.country,
      ].join("")
    : "";

  return {
    address,
    isLoading,
  } as const;
};

const checkSession = (
  error: FirestoreError,
  userId: string,
  callback: () => void,
) => {
  if (!error || !userId) {
    return;
  }

  switch (error?.code) {
    case "permission-denied":
    case "unauthenticated":
      console.warn(
        `Got '${error.code}' error from firebase. Assuming firebase token is invalid so logging user '${userId}' out. Message:  ${error?.message}`,
      );

      callback && callback();
      break;
    default:
      console.warn(
        `Unknown error while fetching profile`,
        error?.code,
        error?.message,
      );
  }
};

/**
 * Countries that, under the Isle of Man licence, are treated as regulated
 * markets requiring ID verification before deposit limits, deposit periods and
 * withdrawals are unlocked.
 */
const IOM_REGULATED_MARKET_COUNTRIES = ["NZ", "CA"];

/**
 * True when the customer holds the Isle of Man licence and resides in one of the
 * IoM-regulated markets.
 */
const useIsProfileIomRegulatedMarket = () => {
  const licence = useCustomerLicence();
  const { profile, isLoading } = useProfile();
  const country = profile?.country;

  const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;
  const isRegulatedMarket =
    country && IOM_REGULATED_MARKET_COUNTRIES.includes(country);
  const isProfileIomRegulatedMarket = isIOM && isRegulatedMarket;

  return { isProfileIomRegulatedMarket, isLoading };
};

/**
 * True when the user is in an IoM-regulated market, where verification is
 * possible, and is not verified.
 */
const useIsProfileIomRegulatedMarketUnverified = () => {
  const { profile, isLoading: profileLoading } = useProfile();
  const { isProfileIomRegulatedMarket, isLoading } =
    useIsProfileIomRegulatedMarket();
  const isVerified = useIsVerified();

  return {
    isProfileIomRegulatedMarketUnverified: Boolean(
      profile?.userVerificationStatus !== "NOT_REQUIRED" &&
        isProfileIomRegulatedMarket &&
        !isVerified,
    ),
    isLoading: isLoading || profileLoading,
  };
};

/**
 * True when the user is in an IoM-regulated market, where verification is
 * possible, and is PARTIALLY_VERIFIED_ELECTRONIC.
 */
const useIsProfileIomRegulatedMarketPartiallyVerified = () => {
  const { profile, isLoading: profileLoading } = useProfile();
  const { isProfileIomRegulatedMarket, isLoading } =
    useIsProfileIomRegulatedMarket();

  return {
    isProfileIomRegulatedMarketPartiallyVerified: Boolean(
      profile?.userVerificationStatus === "PARTIALLY_VERIFIED_ELECTRONIC" &&
        isProfileIomRegulatedMarket,
    ),
    isLoading: isLoading || profileLoading,
  };
};

export {
  useProfile,
  type Profile,
  useProfileAddress,
  useCustomerLicence,
  useIsProfileIomRegulatedMarket,
  useIsProfileIomRegulatedMarketUnverified,
  useIsProfileIomRegulatedMarketPartiallyVerified,
};
